import React, { useState } from "react";
import { Table } from "antd";
import Header from "../Header";
import Sidebar from "../Sidebar";
import { pdficon, pdficon3, refreshicon } from "../imagepath";
import { Link } from "react-router-dom";
import FeatherIcon from "feather-icons-react/build/FeatherIcon";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { getCurrentMonthStartEndIST } from "../../utils/constant/constant";
import { getPurchaseOrderReport } from "../../connections/getPurchaseOrderReport";
import jsPDF from "jspdf";
import "jspdf-autotable";

const { startDateOfMonth, endDateOfMonth } = getCurrentMonthStartEndIST();

const formatDateDDMMYYYY = (dateStr) => {
  if (!dateStr) return "—";
  const clean = String(dateStr).split("T")[0].split(" ")[0];
  const parts = clean.split("-");
  if (parts.length === 3) {
    return `${parts[2].padStart(2, "0")}/${parts[1].padStart(2, "0")}/${parts[0]}`;
  }
  return dateStr;
};

const parseRowItem = (item) => {
  const rate = Number(item.rate || 0);
  const qty = Number(item.quantity || 0);
  const disc = Number(item.discount || 0);
  const subtotal = rate * qty;
  const taxableAmount =
    item.taxable_amount != null
      ? Number(item.taxable_amount)
      : Math.max(0, subtotal - disc);

  const cgstPct = Number(item.cgst || 0);
  const sgstPct = Number(item.sgst || 0);
  const cgstVal =
    item.cgst_amount != null
      ? Number(item.cgst_amount)
      : (taxableAmount * cgstPct) / 100;
  const sgstVal =
    item.sgst_amount != null
      ? Number(item.sgst_amount)
      : (taxableAmount * sgstPct) / 100;
  const grandTotal =
    item.amount != null
      ? Number(item.amount)
      : taxableAmount + cgstVal + sgstVal;

  return {
    billNo: item.bill_number || item.purchase_order_id || item.id || "—",
    dateStr: formatDateDDMMYYYY(item.purchase_date || item.created_at),
    customerName: item.supplier_name || "—",
    gstinNo: item.gst_no || "—",
    taxableAmount,
    cgstAmount: cgstVal,
    sgstAmount: sgstVal,
    grandTotal,
  };
};

const PurchaseOrderReport = () => {
  const {
    user: { user },
  } = useSelector((store) => store);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [startDate, setStartDate] = useState(startDateOfMonth);
  const [endDate, setEndDate] = useState(endDateOfMonth);
  const [exporting, setExporting] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["getPurchaseOrderReport", page, pageSize, startDate, endDate],
    queryFn: () =>
      getPurchaseOrderReport(user?.token, page, pageSize, startDate, endDate, "export"),
    enabled: !!user?.token,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const items = data?.payload?.purchase_items ?? [];

  const totals = items.reduce(
    (acc, item) => {
      const p = parseRowItem(item);
      acc.taxable += p.taxableAmount;
      acc.cgst += p.cgstAmount;
      acc.sgst += p.sgstAmount;
      acc.grandTotal += p.grandTotal;
      return acc;
    },
    { taxable: 0, cgst: 0, sgst: 0, grandTotal: 0 }
  );

  const fetchExportData = async () => {
    setExporting(true);
    try {
      const res = await getPurchaseOrderReport(
        user?.token,
        1,
        50,
        startDate,
        endDate,
        "export"
      );
      return res?.payload?.purchase_items ?? [];
    } catch {
      toast.error("Failed to fetch export data");
      return [];
    } finally {
      setExporting(false);
    }
  };

  const handleCsvExport = async () => {
    const exportItems = await fetchExportData();
    if (!exportItems.length) return;
    const header = [
      "Bill No.",
      "Date",
      "Customer Name",
      "GSTIN No.",
      "Taxb. Amt.",
      "CGST",
      "SGST",
      "Invoice Value",
    ];
    const rows = exportItems.map((item) => {
      const p = parseRowItem(item);
      return [
        `"${p.billNo}"`,
        `"${p.dateStr}"`,
        `"${p.customerName.replace(/"/g, '""')}"`,
        `"${p.gstinNo}"`,
        p.taxableAmount.toFixed(2),
        p.cgstAmount.toFixed(2),
        p.sgstAmount.toFixed(2),
        p.grandTotal.toFixed(2),
      ];
    });

    const totalsRow = exportItems.reduce(
      (acc, item) => {
        const p = parseRowItem(item);
        acc.taxable += p.taxableAmount;
        acc.cgst += p.cgstAmount;
        acc.sgst += p.sgstAmount;
        acc.grand += p.grandTotal;
        return acc;
      },
      { taxable: 0, cgst: 0, sgst: 0, grand: 0 }
    );

    rows.push([
      '"Total"',
      '""',
      '""',
      '""',
      totalsRow.taxable.toFixed(2),
      totalsRow.cgst.toFixed(2),
      totalsRow.sgst.toFixed(2),
      totalsRow.grand.toFixed(2),
    ]);

    const csvContent = [header.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `purchase_order_report_${startDate}_to_${endDate}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePdfExport = async () => {
    const exportItems = await fetchExportData();
    if (!exportItems.length) return;

    const doc = new jsPDF({ unit: "mm", format: "a4" });

    doc.setFontSize(14);
    doc.setFont(undefined, "bold");
    doc.text("Purchase Order Report", doc.internal.pageSize.getWidth() / 2, 14, {
      align: "center",
    });
    doc.setFontSize(10);
    doc.setFont(undefined, "normal");
    doc.text("Inward Supplies Report", doc.internal.pageSize.getWidth() / 2, 20, {
      align: "center",
    });
    doc.text(
      `From Date ${formatDateDDMMYYYY(startDate)} to ${formatDateDDMMYYYY(endDate)}`,
      doc.internal.pageSize.getWidth() / 2,
      26,
      { align: "center" }
    );

    const headers = ["Bill No.", "Date", "Customer Name", "GSTIN No.", "Taxb. Amt.", "CGST", "SGST", "Invoice Value"];
    const bodyData = exportItems.map((item) => {
      const p = parseRowItem(item);
      return [
        p.billNo,
        p.dateStr,
        p.customerName,
        p.gstinNo,
        p.taxableAmount.toFixed(2),
        p.cgstAmount.toFixed(2),
        p.sgstAmount.toFixed(2),
        p.grandTotal.toFixed(2),
      ];
    });

    const totalTaxable = exportItems.reduce((s, i) => s + parseRowItem(i).taxableAmount, 0);
    const totalCgst = exportItems.reduce((s, i) => s + parseRowItem(i).cgstAmount, 0);
    const totalSgst = exportItems.reduce((s, i) => s + parseRowItem(i).sgstAmount, 0);
    const totalGrand = exportItems.reduce((s, i) => s + parseRowItem(i).grandTotal, 0);

    bodyData.push([
      { content: "Total:", colSpan: 4, styles: { fontStyle: "bold", halign: "right", fillColor: [240, 240, 240] } },
      { content: totalTaxable.toFixed(2), styles: { fontStyle: "bold", fillColor: [240, 240, 240] } },
      { content: totalCgst.toFixed(2), styles: { fontStyle: "bold", fillColor: [240, 240, 240] } },
      { content: totalSgst.toFixed(2), styles: { fontStyle: "bold", fillColor: [240, 240, 240] } },
      { content: totalGrand.toFixed(2), styles: { fontStyle: "bold", fillColor: [240, 240, 240] } },
    ]);

    doc.autoTable({
      startY: 32,
      head: [headers],
      body: bodyData,
      theme: "grid",
      headStyles: {
        fillColor: [249, 249, 249],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        fontSize: 9,
        lineWidth: 0.3,
        lineColor: [0, 0, 0],
      },
      bodyStyles: {
        fontSize: 8,
        textColor: [0, 0, 0],
        lineWidth: 0.3,
        lineColor: [0, 0, 0],
      },
      columnStyles: {
        0: { cellWidth: 18 },
        1: { cellWidth: 18, halign: "center" },
        2: { cellWidth: 42 },
        3: { cellWidth: 26 },
        4: { cellWidth: 20, halign: "right" },
        5: { cellWidth: 16, halign: "right" },
        6: { cellWidth: 16, halign: "right" },
        7: { cellWidth: 24, halign: "right" },
      },
      margin: { top: 32, right: 10, bottom: 15, left: 10 },
      didDrawPage: (pageData) => {
        doc.setFontSize(9);
        doc.text(
          `Page ${pageData.pageNumber} of ${pageData.pageCount}`,
          doc.internal.pageSize.getWidth() - 10,
          10,
          { align: "right" }
        );
      },
    });

    doc.save(`purchase_order_report_${startDate}_to_${endDate}.pdf`);
  };

  const columns = [
    {
      title: "Bill No.",
      key: "billNo",
      render: (_, record) => parseRowItem(record).billNo,
    },
    {
      title: "Date",
      key: "dateStr",
      render: (_, record) => parseRowItem(record).dateStr,
    },
    {
      title: "Customer Name",
      key: "customerName",
      render: (_, record) => parseRowItem(record).customerName,
    },
    {
      title: "GSTIN No.",
      key: "gstinNo",
      render: (_, record) => parseRowItem(record).gstinNo,
    },
    {
      title: "Taxb. Amt.",
      key: "taxableAmount",
      align: "right",
      render: (_, record) => parseRowItem(record).taxableAmount.toFixed(2),
    },
    {
      title: "CGST",
      key: "cgstAmount",
      align: "right",
      render: (_, record) => parseRowItem(record).cgstAmount.toFixed(2),
    },
    {
      title: "SGST",
      key: "sgstAmount",
      align: "right",
      render: (_, record) => parseRowItem(record).sgstAmount.toFixed(2),
    },
    {
      title: "Invoice Value",
      key: "grandTotal",
      align: "right",
      render: (_, record) => (
        <strong>{parseRowItem(record).grandTotal.toFixed(2)}</strong>
      ),
    },
  ];

  const summaryRow = () =>
    items.length > 0 ? (
      <Table.Summary.Row style={{ backgroundColor: "#f0f0f0", fontWeight: 700 }}>
        <Table.Summary.Cell index={0} colSpan={4} align="right">
          Total:
        </Table.Summary.Cell>
        <Table.Summary.Cell index={4} align="right">
          {totals.taxable.toFixed(2)}
        </Table.Summary.Cell>
        <Table.Summary.Cell index={5} align="right">
          {totals.cgst.toFixed(2)}
        </Table.Summary.Cell>
        <Table.Summary.Cell index={6} align="right">
          {totals.sgst.toFixed(2)}
        </Table.Summary.Cell>
        <Table.Summary.Cell index={7} align="right">
          <strong>{totals.grandTotal.toFixed(2)}</strong>
        </Table.Summary.Cell>
      </Table.Summary.Row>
    ) : null;

  return (
    <>
      <Header />
      <Sidebar activeClassName="purchase-order-report" />
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="#">Export Report</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <FeatherIcon icon="chevron-right" />
                  </li>
                  <li className="breadcrumb-item active">Purchase Order Report</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-sm-12">
              <div className="card card-table show-entire">
                <div className="card-body">
                  <div className="page-table-header mb-2">
                    <div className="row align-items-center">
                      <div className="col">
                        <div className="doctor-table-blk">
                          <h3>Purchase Order Report</h3>
                          <div className="doctor-search-blk">
                            <div className="add-group">
                              <Link
                                onClick={refetch}
                                className="btn btn-primary add-pluss ms-2"
                              >
                                <img src={refreshicon} alt="#" />
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-auto text-end float-end ms-auto download-grp">
                        <img
                          className="me-2"
                          onClick={exporting ? undefined : handlePdfExport}
                          src={pdficon}
                          alt="#"
                          style={{
                            cursor: exporting ? "not-allowed" : "pointer",
                            opacity: exporting ? 0.5 : 1,
                          }}
                        />
                        <img
                          onClick={exporting ? undefined : handleCsvExport}
                          className="me-2"
                          src={pdficon3}
                          alt="#"
                          style={{
                            cursor: exporting ? "not-allowed" : "pointer",
                            opacity: exporting ? 0.5 : 1,
                          }}
                        />
                      </div>
                    </div>

                    <div className="row mt-2">
                      <div className="col-auto d-flex align-items-center gap-2">
                        <label className="mb-0 me-1">Start Date:</label>
                        <input
                          type="date"
                          className="form-control form-control-sm"
                          style={{ width: 160 }}
                          value={startDate}
                          onChange={(e) => {
                            setStartDate(e.target.value);
                            setPage(1);
                          }}
                        />
                        <label className="mb-0 me-1 ms-2">End Date:</label>
                        <input
                          type="date"
                          className="form-control form-control-sm"
                          style={{ width: 160 }}
                          value={endDate}
                          onChange={(e) => {
                            setEndDate(e.target.value);
                            setPage(1);
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="table-responsive doctor-list">
                    <Table
                      loading={isLoading}
                      pagination={{
                        current: page,
                        pageSize: pageSize,
                        total: data?.meta?.total_count,
                        showTotal: (total, range) =>
                          `Showing ${range[0]} to ${range[1]} of ${total} entries`,
                        onChange: (newPage, newPageSize) => {
                          setPage(newPage);
                          setPageSize(newPageSize);
                        },
                      }}
                      columns={columns}
                      dataSource={items}
                      rowKey={(record) => record.id}
                      summary={summaryRow}
                      scroll={{ x: "max-content" }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PurchaseOrderReport;
